/**
 * Calendar Mappings Service
 *
 * API client for calendar mapping management (Map & Shoot workflow)
 */

import api from './api';
import {
  CalendarMapping,
  CreateMappingData,
  MoveDateData,
  MoveCalendarData,
  ApplyRulesData,
  CalendarViewParams,
  CalendarViewResponse,
  CalendarMappingApiResponse,
} from '@/types/calendar-mappings';

/**
 * Format date to ISO 8601 string
 */
const formatDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    return new Date(date).toISOString();
  }
  return date.toISOString();
};

export const calendarMappingsService = {
  /**
   * Map post/variation to calendar
   * POST /api/calendar-mappings
   */
  createMapping: async (data: CreateMappingData): Promise<CalendarMapping> => {
    const payload = {
      ...data,
      plannedDate: formatDate(data.plannedDate),
    };

    const { data: response } = await api.post<CalendarMappingApiResponse<CalendarMapping>>(
      '/api/calendar-mappings',
      payload
    );
    return response.data;
  },

  /**
   * Get calendar view (all mappings for month, grouped by date)
   * GET /api/calendar-mappings/calendar-view?calendarId=X&month=YYYY-MM
   */
  getCalendarView: async (params: CalendarViewParams): Promise<CalendarViewResponse> => {
    const queryParams = new URLSearchParams({
      calendarId: params.calendarId.toString(),
      month: params.month,
    });

    const { data } = await api.get<CalendarViewResponse>(
      `/api/calendar-mappings/calendar-view?${queryParams.toString()}`
    );
    return data;
  },

  /**
   * Get single mapping by ID
   * GET /api/calendar-mappings/:id
   */
  getMapping: async (id: number): Promise<CalendarMapping> => {
    const { data } = await api.get<CalendarMappingApiResponse<CalendarMapping>>(
      `/api/calendar-mappings/${id}`
    );
    return data.data;
  },

  /**
   * Move mapping to different date
   * PATCH /api/calendar-mappings/:id/move-date
   */
  moveDate: async (id: number, moveData: MoveDateData): Promise<CalendarMapping> => {
    const payload = {
      newDate: formatDate(moveData.newDate),
    };

    const { data } = await api.patch<CalendarMappingApiResponse<CalendarMapping>>(
      `/api/calendar-mappings/${id}/move-date`,
      payload
    );
    return data.data;
  },

  /**
   * Move mapping to different calendar
   * PATCH /api/calendar-mappings/:id/move-calendar
   */
  moveCalendar: async (
    id: number,
    moveData: MoveCalendarData
  ): Promise<CalendarMapping> => {
    const { data } = await api.patch<CalendarMappingApiResponse<CalendarMapping>>(
      `/api/calendar-mappings/${id}/move-calendar`,
      moveData
    );
    return data.data;
  },

  /**
   * Delete mapping
   * DELETE /api/calendar-mappings/:id
   */
  deleteMapping: async (id: number): Promise<void> => {
    await api.delete(`/api/calendar-mappings/${id}`);
  },

  /**
   * Mark mapping as ready to shoot
   * POST /api/calendar-mappings/:id/ready-to-shoot
   */
  markReadyToShoot: async (id: number): Promise<CalendarMapping> => {
    const { data } = await api.post<CalendarMappingApiResponse<CalendarMapping>>(
      `/api/calendar-mappings/${id}/ready-to-shoot`
    );
    return data.data;
  },

  /**
   * Apply mapping rules (auto-distribute variations)
   * POST /api/calendar-mappings/apply-rules
   */
  applyMappingRules: async (rulesData: ApplyRulesData): Promise<CalendarMapping[]> => {
    const payload = {
      ...rulesData,
      plannedDate: formatDate(rulesData.plannedDate),
    };

    const { data } = await api.post<CalendarMappingApiResponse<CalendarMapping[]>>(
      '/api/calendar-mappings/apply-rules',
      payload
    );
    return data.data;
  },

  /**
   * Bulk map multiple posts to a calendar date
   * POST /api/calendar-mappings/bulk-map
   */
  bulkMap: async (
    postIds: number[],
    calendarId: number,
    plannedDate: Date | string
  ): Promise<{ mappedCount: number; skippedCount: number; errors: string[] }> => {
    const payload = {
      postIds,
      calendarId,
      plannedDate: formatDate(plannedDate),
    };

    const { data } = await api.post<{
      success: boolean;
      message: string;
      data: { mappedCount: number; skippedCount: number; errors: string[] };
    }>('/api/calendar-mappings/bulk-map', payload);
    return data.data;
  },
};
